import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import OidcError from '@/components/auth/oidc-error';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { normalizeAuthErrorDetail, normalizeAuthErrorReason, withoutAuthErrorParams } from '@/lib/auth/auth-error';
import { redirectToLogin, startTokenRefreshLoop, stopTokenRefreshLoop } from '@/lib/auth/token-refresh';

type OidcGateState = 'checking' | 'authenticated' | 'redirecting' | 'error';

export const AuthGate = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const authError = searchParams.get('authError');
  const authErrorDetail = searchParams.get('authErrorDetail');
  const [state, setState] = useState<OidcGateState>('checking');
  // Snapshot of the reason at the moment the card is shown — the URL param is stripped
  // right after (see below), so the card can't read it from the location anymore.
  const [errorReason, setErrorReason] = useState<string | null>(null);
  // Stripping the param re-fires the effect below (authError → null); without this guard
  // that re-run would treat the error card as "no session, no error" and auto-redirect
  // to login — the endless-retry loop the card exists to prevent.
  const erroredRef = useRef(false);

  useEffect(() => {
    if (erroredRef.current) return;
    let cancelled = false;
    startTokenRefreshLoop().then((ok) => {
      if (cancelled) return;
      if (ok) {
        setState('authenticated');
        return;
      }
      if (authError) {
        // A failed sign-in with no session to fall back on — show the card instead of
        // auto-redirecting, which would loop endlessly against a genuinely broken IdP.
        // Reason in the message so each failure mode groups as its own Sentry issue;
        // full server-side diagnostics live in the Pages Function logs.
        const reason = normalizeAuthErrorReason(authError);
        const detail = normalizeAuthErrorDetail(authErrorDetail);
        Sentry.captureMessage(`Sign-in failed: ${reason}${detail ? ` (${detail})` : ''}`, {
          level: 'error',
          tags: { auth_error_reason: reason, auth_error_detail: detail },
        });
        erroredRef.current = true;
        setErrorReason(reason);
        setState('error');
        return;
      }
      setState('redirecting');
      redirectToLogin();
    });

    return () => {
      cancelled = true;
      stopTokenRefreshLoop();
    };
  }, [authError, authErrorDetail]);

  // `?authError=` can be stale: when concurrent sign-in flows race, a losing tab lands
  // here just before/after the winning tab establishes the session. Only trust the flag
  // once the session check has confirmed there's really nothing to fall back on — then
  // strip it: once authenticated so it can't leak into a later login's returnTo, and once
  // the card is shown so reloads and history revisits don't re-report the old failure.
  useEffect(() => {
    if ((state !== 'authenticated' && state !== 'error') || !authError) return;
    setSearchParams(withoutAuthErrorParams(searchParams), { replace: true });
  }, [state, authError, searchParams, setSearchParams]);

  if (state === 'error') return <OidcError message={errorReason} />;
  if (state === 'authenticated') return <Outlet />;

  // 'checking' and 'redirecting' both show the same full-page spinner — the
  // latter is only on screen for the moment before the real browser navigation
  // in the effect above takes over.
  return (
    <div className="grid min-h-dvh place-items-center">
      <LoadingPanel label="Signing in" />
    </div>
  );
};
AuthGate.displayName = 'AuthGate';
